import type { UserPermissions } from "@redotech/account-common/permissions";
import { isRedoEmailAddress } from "@redotech/account-common/redo-domains";
import { useRequiredContext } from "@redotech/react-util/context";
import { isSettingsReportsPageRetired } from "@redotech/redo-fulfillment-common/label-purchases-report-access";
import { RegistrationRewardType } from "@redotech/redo-return-warranties-common/extended-warranty";
import {
  SETTINGS_FLAGS,
  type SettingsExperience,
  type SettingsFact,
  type SettingsFlag,
  type SettingsVisibilityFacts,
} from "@redotech/redo-settings-common/settings-visibility/conditions";
import {
  isSettingsPageVisible,
  isSettingsSectionVisible,
  type SettingsPageId,
  type SettingsSectionId,
} from "@redotech/redo-settings-common/settings-visibility/settings-pages";
import { isShopifyEmail } from "@redotech/redo-shopify-common/shopify-email";
import { useContext, useMemo } from "react";
import { PermissionsContext } from "../auth/permissions-context";
import { TeamContext } from "../team";
import { useEnabledCapabilities } from "../use-capability";
import { useIsRedoSuppliesTeam } from "../use-is-redo-supplies-team";
import { useTeamFeatureFlag } from "../use-team-feature-flag";
import { UserContext } from "../user";
import { isRestrictedShippingTeam } from "./restricted-shipping-teams";

export interface SettingsVisibility {
  page: (page: SettingsPageId) => boolean;
  section: (section: SettingsSectionId) => boolean;
}

const NO_PERMISSIONS: ReadonlySet<UserPermissions> = new Set();

/** Everything settings visibility reads, gathered once for the active team and user. */
function useSettingsVisibilityFacts(): SettingsVisibilityFacts {
  const team = useRequiredContext(TeamContext);
  const user = useContext(UserContext);
  const permissions = useContext(PermissionsContext) ?? NO_PERMISSIONS;
  const capabilities = useEnabledCapabilities();
  const redoSuppliesTeam = useIsRedoSuppliesTeam();
  const analyticsLegacyReportsSettings = useTeamFeatureFlag(
    SETTINGS_FLAGS.analyticsLegacyReportsSettings,
    { forceEnabledForRedoUsers: false },
  ).enabled;
  const imsQuickbooks = useTeamFeatureFlag(SETTINGS_FLAGS.imsQuickbooks, {
    forceEnabledForRedoUsers: false,
  }).enabled;

  return useMemo(() => {
    const flags = new Set<SettingsFlag>();
    if (analyticsLegacyReportsSettings) {
      flags.add("analyticsLegacyReportsSettings");
    }
    if (imsQuickbooks) {
      flags.add("imsQuickbooks");
    }
    const experiences = new Set<SettingsExperience>();
    const settings = team.settings;
    const teamFacts: Record<SettingsFact, boolean> = {
      redoSuppliesTeam,
      restrictedShippingTeam: isRestrictedShippingTeam(team._id.toString()),
      settingsReportsPageRetired: isSettingsReportsPageRetired(new Date()),
      shopifyEmailTeam: isShopifyEmail(team.email),
      supportAiSetting: !!settings.support?.ai?.enabled,
      warrantyRegistrationRewards:
        settings.warranties?.registrationRewardType !==
        RegistrationRewardType.DISABLED,
      wmsSourceOfTruth:
        settings.outboundLabels?.wms?.sourceOfTruthEnabled === true,
    };
    const facts = new Set<SettingsFact>();
    for (const [name, value] of Object.entries(teamFacts)) {
      if (value && isSettingsFact(name)) {
        facts.add(name);
      }
    }
    return {
      capabilities,
      permissions,
      flags,
      experiences,
      facts,
      teamType: team.teamType ?? null,
      platform: team.platform ?? null,
      staff: isRedoEmailAddress(user?.email),
    };
  }, [
    team,
    user,
    permissions,
    capabilities,
    redoSuppliesTeam,
    analyticsLegacyReportsSettings,
    imsQuickbooks,
  ]);
}

const SETTINGS_FACT_NAMES: Record<SettingsFact, true> = {
  redoSuppliesTeam: true,
  restrictedShippingTeam: true,
  settingsReportsPageRetired: true,
  shopifyEmailTeam: true,
  supportAiSetting: true,
  warrantyRegistrationRewards: true,
  wmsSourceOfTruth: true,
};

function isSettingsFact(name: string): name is SettingsFact {
  return Object.hasOwn(SETTINGS_FACT_NAMES, name);
}

/** The one way settings UI decides what shows: pages, sections, routes and tabs all ask this. */
export function useSettingsVisibility(): SettingsVisibility {
  const facts = useSettingsVisibilityFacts();
  return useMemo(
    () => ({
      page: (page) => isSettingsPageVisible(page, facts),
      section: (section) => isSettingsSectionVisible(section, facts),
    }),
    [facts],
  );
}
